import { observer } from "mobx-react";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useAppStore } from "../../../hooks/useAppStore";
import { useChatCategoryStore } from "../../../hooks/useChatCategoryStore";
import { useTicketStore } from "../../../hooks/useTicketStore";
import ContactSupportFormContent from "./contact-support-components/ContactSupportFormContent";
import {
  CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH,
  CONTACT_SUPPORT_PURPOSE_TYPES,
  ContactSupportFormType,
} from "../../../config/constants";
import { CloseIcon } from "../../icons";
import { ContactSupportUploadFile } from "../../../stores/AppStore";
import SidebarDelimiter from "../../SidebarDelimiter";
import ResizePointerShim from "../ResizePointerShim";

interface ContactSupportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const initialFormData: ContactSupportFormType = {
  patientName: "",
  mrn: "",
};

/**
 * Validate customer support form data
 * Checks for empthy values and length limits of the fields
 * @returns true if form is invalid, false otherwise
 */
const isFormInvalid = (
  formData: ContactSupportFormType,
  purpose: string,
  uploadedFiles: ContactSupportUploadFile[],
): boolean => {
  if (!purpose) {
    return true;
  }

  // Support purpose validation
  if (purpose === CONTACT_SUPPORT_PURPOSE_TYPES.SUPPORT) {
    return (
      !formData.subject?.trim() ||
      !formData.comment?.trim() ||
      formData?.subject?.length >
        CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.subject ||
      formData?.comment?.length > CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.comment
    );
  }

  // Mandatory fields for other purposes
  if (
    !formData?.patientName?.trim() ||
    !formData?.mrn?.trim() ||
    uploadedFiles?.filter((file) => file.status !== "uploaded")?.length === 0 ||
    formData?.patientName?.length >
      CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.patientName ||
    formData?.mrn?.length > CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.mrn
  ) {
    return true;
  }

  switch (purpose) {
    case CONTACT_SUPPORT_PURPOSE_TYPES.PRESERVICE_REVIEW:
      return !formData?.surgeryDate;
    case CONTACT_SUPPORT_PURPOSE_TYPES.DENIAL_MANAGEMENT: {
      return (
        !formData?.claimNumber?.trim() ||
        formData?.claimNumber?.length >
          CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.claimNumber ||
        !formData?.denialType?.trim()
      );
    }
  }

  return false;
};

const ContactSupportDialog: FC<ContactSupportDialogProps> = observer(
  ({ isOpen, onClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [purpose, setPurpose] = useState<string>("");
    const [formData, setFormData] =
      useState<ContactSupportFormType>(initialFormData);

    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [responseStatus, setResponseStatus] = useState<{
      status: "success" | "error" | "warning";
      message?: string;
    } | null>(null);

    const {
      contactSupportUploadFiles: uploadedFiles,
      setContactSupportUploadFiles: setUploadedFiles,
      triggerUploadContactSupportFiles,
      isUploadSuccessful,
      setIsUploadSuccessful,
    } = useAppStore();

    const {
      sidePanelWidth,
      isSidePanelResizing,
      setSidePanelWidth,
      setIsSidePanelResizing,
    } = useChatCategoryStore();

    const { seedTicket } = useTicketStore();

    const handleMouseMove = useCallback((e: MouseEvent) => {
      const containerRef = document.getElementById("tickets-page-container");
      if (!containerRef) return;

      const containerRect = containerRef.getBoundingClientRect();
      const newWidth = containerRect.right - e.clientX;
      const newPercent = (newWidth / containerRect.width) * 100;

      setSidePanelWidth(newPercent);
    }, []);

    const handleMouseUp = useCallback(() => {
      setIsSidePanelResizing(false);
    }, []);

    useEffect(() => {
      if (!isSidePanelResizing) return;

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }, [isSidePanelResizing, handleMouseMove, handleMouseUp]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      setIsSidePanelResizing(true);
    }, []);

    const isSendDisabled = useMemo(
      () => isFormInvalid(formData, purpose, uploadedFiles),
      [formData, purpose, uploadedFiles],
    );

    useEffect(() => {
      if (isOpen) {
        resetStates();
      }
    }, [isOpen]);

    const resetStates = () => {
      setPurpose("");
      setFormData(initialFormData);
      setUploadedFiles([]);
      setIsUploading(false);
      setResponseStatus(null);
      setIsUploadSuccessful(false);
    };

    const handleClose = () => {
      onClose();
      resetStates();
    };

    const handleOpenNewTicket = () => {
      resetStates();
    };

    const handleConfirm = async () => {
      setIsUploading(true);
      setResponseStatus(null);

      const response = await triggerUploadContactSupportFiles({
        ...formData,
        purpose,
      } as any);

      if (response.ticket) seedTicket(response.ticket);
      setResponseStatus(response);
      setIsUploading(false);
    };

    if (!isOpen) return null;

    const panelContent = (
      <>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 1.5,
              flexShrink: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              New Request
            </Typography>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "& svg": { width: 20, height: 20 },
                }}
              >
                <CloseIcon className="" />
              </Box>
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: 3,
              py: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            {isUploadSuccessful ? (
              <Alert severity="success">
                Your ticket was successfully submitted to the support team.
              </Alert>
            ) : (
              <>
                <ContactSupportFormContent
                  purpose={purpose}
                  setPurpose={setPurpose}
                  formData={formData}
                  setFormData={setFormData}
                  isDisabled={isUploading}
                />

                {responseStatus && responseStatus.message ? (
                  <Alert severity={responseStatus.status}>
                    {responseStatus.message}
                  </Alert>
                ) : null}
              </>
            )}
          </Box>

          <Box
            sx={{
              px: 3,
              py: 2,
              flexShrink: 0,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {isUploadSuccessful ? (
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button onClick={handleClose} variant="outlined" fullWidth>
                  Close
                </Button>
                <Button
                  onClick={handleOpenNewTicket}
                  variant="contained"
                  fullWidth
                >
                  Open New Ticket
                </Button>
              </Box>
            ) : (
              <Button
                onClick={handleConfirm}
                disabled={isSendDisabled || isUploading}
                variant="contained"
                fullWidth
                loading={isUploading}
                sx={{ py: 1.25 }}
              >
                Send
              </Button>
            )}
          </Box>
        </Box>
      </>
    );

    if (isMobile) {
      return (
        <SwipeableDrawer
          anchor="right"
          open={isOpen}
          onClose={handleClose}
          onOpen={() => {}}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: "100%",
              maxWidth: "100vw",
              display: "flex",
              flexDirection: "row",
            },
          }}
        >
          {panelContent}
        </SwipeableDrawer>
      );
    }

    return (
      <>
        <ResizePointerShim active={isSidePanelResizing} />
        <Box
          component="aside"
          sx={{
            height: "100%",
            flexShrink: 0,
            display: "flex",
            flexDirection: "row",
            width: `${sidePanelWidth}%`,
            borderLeft: `1px solid ${theme.palette.divider}`,
            backgroundColor: "background.paper",
            transition: isSidePanelResizing ? "none" : "width 150ms ease-out",
          }}
        >
          <SidebarDelimiter
            onResizeStart={handleMouseDown}
            isResizing={isSidePanelResizing}
          />
          {panelContent}
        </Box>
      </>
    );
  },
);

export default ContactSupportDialog;
